import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Plus, ChevronLeft, ChevronRight, Check, Clock, MapPin, Phone, Repeat, Trash2, CalendarDays, ListChecks, Camera, Users
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PhotoGallery from "@/components/PhotoGallery";
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addDays,
} from "date-fns";

const SERVICE_TYPES = ["Mowing", "Edging", "Leaf Removal", "Mulching", "Shrub Trimming", "Yard Cleanup", "Other"];
const FREQUENCIES = [
  { value: "once", label: "One-Time" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Monthly" },
];

function toYmd(d) {
  return format(d, "yyyy-MM-dd");
}

const emptyJob = {
  customer_id: "",
  service_type: "Mowing",
  scheduled_date: toYmd(new Date()),
  scheduled_time: "",
  duration_minutes: 60,
  price: 0,
  notes: "",
  frequency: "once",
  end_date: "",
};

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("route"); // route | calendar
  const [cursor, setCursor] = useState(new Date()); // month cursor for calendar / day cursor for route
  const [routeDate, setRouteDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyJob);
  const [saving, setSaving] = useState(false);
  const [photosJob, setPhotosJob] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ ...emptyJob });
  const [bulkCustomerIds, setBulkCustomerIds] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [js, cs] = await Promise.all([api.get("/jobs"), api.get("/customers")]);
      setJobs(js.data);
      setCustomers(cs.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const jobsByDate = useMemo(() => {
    const map = {};
    for (const j of jobs) {
      if (!map[j.scheduled_date]) map[j.scheduled_date] = [];
      map[j.scheduled_date].push(j);
    }
    return map;
  }, [jobs]);

  const openNewJob = (dateStr) => {
    setForm({ ...emptyJob, scheduled_date: dateStr || toYmd(routeDate), customer_id: customers[0]?.id || "" });
    setDialogOpen(true);
  };

  const onCreateJob = async (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error("Select a customer first");
      return;
    }
    if (form.frequency !== "once" && !form.end_date) {
      toast.error("Set an end date for recurring jobs");
      return;
    }
    if (form.end_date && form.end_date < form.scheduled_date) {
      toast.error("End date must be after the start date");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        duration_minutes: Number(form.duration_minutes) || 60,
        end_date: form.frequency === "once" ? null : form.end_date,
      };
      await api.post("/jobs", payload);
      toast.success(form.frequency === "once" ? "Job scheduled" : "Recurring series scheduled");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const completeJob = async (jid) => {
    try {
      const { data } = await api.post(`/jobs/${jid}/complete`);
      if (data.next_occurrence) {
        toast.success(`Completed · Next auto-scheduled for ${data.next_occurrence.scheduled_date}`);
      } else {
        toast.success("Marked complete");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reopenJob = async (jid) => {
    try { await api.post(`/jobs/${jid}/reopen`); load(); } catch {}
  };

  const deleteJob = async (j) => {
    if (j.series_id) {
      const choice = window.prompt(
        `This job belongs to a recurring series. Type:\n\n  "this" to delete just this occurrence\n  "future" to delete this and all upcoming occurrences\n  "all" to delete the ENTIRE series (past + future)\n\n(Cancel to keep everything)`,
        "this"
      );
      if (!choice) return;
      try {
        if (choice === "this") {
          await api.delete(`/jobs/${j.id}`);
          toast.success("Occurrence deleted");
        } else if (choice === "future") {
          await api.delete(`/jobs/series/${j.series_id}`, {
            params: { only_future: true, from_date: j.scheduled_date },
          });
          toast.success("This and all future occurrences deleted");
        } else if (choice === "all") {
          const { data } = await api.delete(`/jobs/series/${j.series_id}`);
          toast.success(`${data.deleted} occurrence${data.deleted === 1 ? "" : "s"} deleted`);
        } else {
          toast.error("Unrecognized option — nothing deleted");
          return;
        }
        load();
      } catch {
        toast.error("Delete failed");
      }
      return;
    }
    if (!window.confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success("Deleted"); load(); } catch {}
  };

  return (
    <div data-testid="schedule-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Field Operations</div>
          <h1 className="font-display text-4xl font-black tracking-tight leading-none">Schedule</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setBulkForm({ ...emptyJob, scheduled_date: toYmd(new Date()) });
              setBulkCustomerIds([]);
              setBulkSearch("");
              setBulkOpen(true);
            }}
            className="bm-btn bm-btn-outline"
            data-testid="mass-schedule-button"
          >
            <Users className="w-4 h-4" /> Mass Schedule
          </button>
          <button onClick={() => openNewJob()} className="bm-btn bm-btn-primary" data-testid="add-job-button">
            <Plus className="w-4 h-4" /> New Job
          </button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList className="bg-[#141c18] border border-[#233129] rounded-sm p-1">
          <TabsTrigger value="route" className="data-[state=active]:bg-[#1e3b2d] data-[state=active]:text-[#eae6db] rounded-sm text-sm" data-testid="tab-route">
            <ListChecks className="w-4 h-4 mr-2" /> Route View
          </TabsTrigger>
          <TabsTrigger value="calendar" className="data-[state=active]:bg-[#1e3b2d] data-[state=active]:text-[#eae6db] rounded-sm text-sm" data-testid="tab-calendar">
            <CalendarDays className="w-4 h-4 mr-2" /> Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="route" className="mt-5">
          <RouteView
            date={routeDate}
            setDate={setRouteDate}
            jobs={jobs}
            jobsByDate={jobsByDate}
            onComplete={completeJob}
            onReopen={reopenJob}
            onDelete={deleteJob}
            onAdd={() => openNewJob(toYmd(routeDate))}
            onOpenPhotos={(j) => setPhotosJob(j)}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-5">
          <CalendarView
            cursor={cursor}
            setCursor={setCursor}
            jobsByDate={jobsByDate}
            onAdd={(d) => openNewJob(toYmd(d))}
            onOpenDay={(d) => { setRouteDate(d); setView("route"); }}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">New Job</DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              Schedule a one-time or recurring job for an existing customer.
            </DialogDescription>
          </DialogHeader>
          {customers.length === 0 ? (
            <div className="text-sm text-[#8ba394] py-4">
              Add a customer first.{" "}
              <Link to="/customers" className="text-[#eae6db] underline decoration-[#c83e2d]">Go to Customers →</Link>
            </div>
          ) : (
            <form onSubmit={onCreateJob} className="space-y-3" data-testid="new-job-form">
              <div>
                <label className="overline block mb-2">Customer *</label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger className="bm-input" data-testid="job-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Service *</label>
                  <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                    <SelectTrigger className="bm-input" data-testid="job-service-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                      {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline block mb-2">Frequency</label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger className="bm-input" data-testid="job-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                      {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Date *</label>
                  <input type="date" required value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="bm-input" data-testid="job-date-input" />
                </div>
                <div>
                  <label className="overline block mb-2">Time</label>
                  <input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} className="bm-input" />
                </div>
              </div>

              {form.frequency !== "once" && (
                <div data-testid="end-date-row">
                  <label className="overline block mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    value={form.end_date}
                    min={form.scheduled_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="bm-input"
                    data-testid="job-end-date-input"
                  />
                  <p className="text-xs text-[#8ba394] mt-1">
                    All occurrences from <span className="font-mono">{form.scheduled_date}</span> through this date will be added to the calendar.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Duration (min)</label>
                  <input type="number" min="15" step="15" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bm-input" />
                </div>
                <div>
                  <label className="overline block mb-2">Price ($)</label>
                  <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bm-input" />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Notes</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bm-input" placeholder="Gate code, instructions…" />
              </div>

              <DialogFooter>
                <button type="button" onClick={() => setDialogOpen(false)} className="bm-btn bm-btn-outline">Cancel</button>
                <button type="submit" disabled={saving} className="bm-btn bm-btn-primary" data-testid="job-submit-button">
                  {saving ? "Scheduling…" : "Schedule Job"}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <PhotoGallery
        open={!!photosJob}
        onOpenChange={(v) => !v && setPhotosJob(null)}
        jobId={photosJob?.id}
        customerName={photosJob?.customer_name}
        onChanged={load}
      />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Mass Schedule</DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              Pick multiple customers and schedule the same job for all of them on the same day.
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="overline block mb-2">
              Customers · <span className="font-mono text-[#e56b42]">{bulkCustomerIds.length}</span> selected
            </label>
            <input
              className="bm-input mb-2"
              placeholder="Filter customers"
              value={bulkSearch}
              onChange={(e) => setBulkSearch(e.target.value)}
              data-testid="bulk-customer-search"
            />
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => {
                  const filtered = customers.filter((c) => !bulkSearch || c.name.toLowerCase().includes(bulkSearch.toLowerCase()));
                  setBulkCustomerIds(filtered.map((c) => c.id));
                }}
                className="bm-btn bm-btn-ghost text-xs"
                data-testid="bulk-select-all"
              >Select all visible</button>
              <button type="button" onClick={() => setBulkCustomerIds([])} className="bm-btn bm-btn-ghost text-xs" data-testid="bulk-clear">Clear</button>
            </div>
            <div className="border border-[#233129] rounded-sm max-h-52 overflow-auto" data-testid="bulk-customer-list">
              {customers
                .filter((c) => !bulkSearch || c.name.toLowerCase().includes(bulkSearch.toLowerCase()) || (c.address || "").toLowerCase().includes(bulkSearch.toLowerCase()))
                .map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#0a0f0d] cursor-pointer border-b border-[#233129] last:border-0">
                    <input
                      type="checkbox"
                      checked={bulkCustomerIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) setBulkCustomerIds([...bulkCustomerIds, c.id]);
                        else setBulkCustomerIds(bulkCustomerIds.filter((id) => id !== c.id));
                      }}
                      data-testid={`bulk-customer-${c.id}`}
                    />
                    <span className="font-medium">{c.name}</span>
                    {c.address && <span className="text-[#8ba394] text-xs ml-auto truncate">{c.address}</span>}
                  </label>
                ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Service *</label>
              <Select value={bulkForm.service_type} onValueChange={(v) => setBulkForm({ ...bulkForm, service_type: v })}>
                <SelectTrigger className="bm-input" data-testid="bulk-service-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                  {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline block mb-2">Frequency</label>
              <Select value={bulkForm.frequency} onValueChange={(v) => setBulkForm({ ...bulkForm, frequency: v })}>
                <SelectTrigger className="bm-input" data-testid="bulk-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                  {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline block mb-2">Date *</label>
              <input type="date" required value={bulkForm.scheduled_date} onChange={(e) => setBulkForm({ ...bulkForm, scheduled_date: e.target.value })} className="bm-input" data-testid="bulk-date-input" />
            </div>
            <div>
              <label className="overline block mb-2">Time</label>
              <input type="time" value={bulkForm.scheduled_time} onChange={(e) => setBulkForm({ ...bulkForm, scheduled_time: e.target.value })} className="bm-input" />
            </div>
            {bulkForm.frequency !== "once" && (
              <div className="col-span-2">
                <label className="overline block mb-2">End Date *</label>
                <input type="date" required value={bulkForm.end_date} min={bulkForm.scheduled_date} onChange={(e) => setBulkForm({ ...bulkForm, end_date: e.target.value })} className="bm-input" data-testid="bulk-end-date-input" />
              </div>
            )}
            <div>
              <label className="overline block mb-2">Duration (min)</label>
              <input type="number" min="15" step="15" value={bulkForm.duration_minutes} onChange={(e) => setBulkForm({ ...bulkForm, duration_minutes: e.target.value })} className="bm-input" />
            </div>
            <div>
              <label className="overline block mb-2">Price ($)</label>
              <input type="number" step="0.01" value={bulkForm.price} onChange={(e) => setBulkForm({ ...bulkForm, price: e.target.value })} className="bm-input" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Notes</label>
            <textarea rows={2} value={bulkForm.notes} onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })} className="bm-input" placeholder="Applies to every job created" />
          </div>

          <DialogFooter>
            <button type="button" onClick={() => setBulkOpen(false)} className="bm-btn bm-btn-outline">Cancel</button>
            <button
              type="button"
              disabled={bulkSaving || bulkCustomerIds.length === 0}
              onClick={async () => {
                if (bulkCustomerIds.length === 0) { toast.error("Select at least one customer"); return; }
                if (bulkForm.frequency !== "once" && !bulkForm.end_date) { toast.error("Set an end date for recurring jobs"); return; }
                setBulkSaving(true);
                try {
                  const payload = {
                    customer_ids: bulkCustomerIds,
                    service_type: bulkForm.service_type,
                    scheduled_date: bulkForm.scheduled_date,
                    scheduled_time: bulkForm.scheduled_time,
                    duration_minutes: Number(bulkForm.duration_minutes) || 60,
                    price: Number(bulkForm.price) || 0,
                    notes: bulkForm.notes,
                    frequency: bulkForm.frequency,
                    end_date: bulkForm.frequency === "once" ? null : bulkForm.end_date,
                  };
                  const { data } = await api.post("/jobs/bulk", payload);
                  toast.success(`Created ${data.created} job${data.created === 1 ? "" : "s"}${data.series_count ? ` (${data.series_count} series)` : ""}`);
                  setBulkOpen(false);
                  load();
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail));
                } finally {
                  setBulkSaving(false);
                }
              }}
              className="bm-btn bm-btn-primary"
              data-testid="bulk-submit-button"
            >
              {bulkSaving ? "Scheduling…" : `Schedule ${bulkCustomerIds.length || "0"} Customer${bulkCustomerIds.length === 1 ? "" : "s"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RouteView({ date, setDate, jobs, jobsByDate, onComplete, onReopen, onDelete, onAdd, onOpenPhotos, loading }) {
  const [mode, setMode] = useState("week"); // "week" (default) | "day"

  const days = mode === "week"
    ? Array.from({ length: 7 }, (_, i) => addDays(date, i))
    : [date];

  const stepDays = mode === "week" ? 7 : 1;
  const headerLabel = mode === "week"
    ? `${format(days[0], "MMM d")} – ${format(days[days.length - 1], "MMM d, yyyy")}`
    : format(date, "EEEE · MMMM d, yyyy");

  const allEmpty = days.every((d) => (jobsByDate[toYmd(d)] || []).length === 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 bm-card p-3 gap-3 flex-wrap">
        <button onClick={() => setDate(addDays(date, -stepDays))} className="bm-btn bm-btn-outline" data-testid="route-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center min-w-0">
          <div className="overline text-[10px]">{mode === "week" ? "Week of" : format(date, "yyyy")}</div>
          <div className="font-display font-black text-lg tracking-tight" data-testid="route-date-label">{headerLabel}</div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex bg-[#0a0f0d] border border-[#233129] rounded-sm overflow-hidden" data-testid="route-mode-toggle">
            <button
              onClick={() => setMode("week")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${mode === "week" ? "bg-[#1e3b2d] text-[#eae6db]" : "text-[#8ba394] hover:text-[#eae6db]"}`}
              data-testid="route-mode-week"
            >Week</button>
            <button
              onClick={() => setMode("day")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${mode === "day" ? "bg-[#1e3b2d] text-[#eae6db]" : "text-[#8ba394] hover:text-[#eae6db]"}`}
              data-testid="route-mode-day"
            >Day</button>
          </div>
          <button onClick={() => setDate(new Date())} className="bm-btn bm-btn-outline hidden sm:inline-flex">Today</button>
          <button onClick={() => setDate(addDays(date, stepDays))} className="bm-btn bm-btn-outline" data-testid="route-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : allEmpty ? (
        <div className="bm-card p-10 text-center" data-testid="route-empty">
          <div className="text-[#8ba394] mb-4">{mode === "week" ? "No jobs scheduled this week." : "No jobs on this day."}</div>
          <button onClick={onAdd} className="bm-btn bm-btn-primary">
            <Plus className="w-4 h-4" /> Schedule a Job
          </button>
        </div>
      ) : (
        <div className="space-y-5" data-testid="route-list">
          {days.map((d) => {
            const ymd = toYmd(d);
            const dayJobs = (jobsByDate[ymd] || []).slice().sort(
              (a, b) => (a.scheduled_time || "zz").localeCompare(b.scheduled_time || "zz")
            );
            if (mode === "week" && dayJobs.length === 0) return null;
            const isToday = toYmd(new Date()) === ymd;
            return (
              <div key={ymd} data-testid={`route-day-${ymd}`}>
                {mode === "week" && (
                  <div className="flex items-center gap-3 mb-2 px-1">
                    <div className={`font-display font-black text-sm tracking-tight ${isToday ? "text-[#c83e2d]" : "text-[#eae6db]"}`}>
                      {format(d, "EEEE · MMM d")}
                    </div>
                    {isToday && <span className="bm-badge bm-badge-cancelled">Today</span>}
                    <span className="text-xs text-[#8ba394] font-mono">{dayJobs.length} job{dayJobs.length !== 1 ? "s" : ""}</span>
                  </div>
                )}
                <div className="space-y-3">
                  {dayJobs.map((j) => (
                    <div key={j.id} className={`bm-card p-4 flex items-start gap-4 ${j.status === "completed" ? "opacity-60" : ""}`} data-testid={`route-job-${j.id}`}>
                      <button
                        onClick={() => (j.status === "completed" ? onReopen(j.id) : onComplete(j.id))}
                        className={`w-10 h-10 border-2 rounded-sm flex items-center justify-center shrink-0 transition-colors ${
                          j.status === "completed"
                            ? "bg-[#2d6a4f] border-[#2d6a4f]"
                            : "border-[#233129] hover:border-[#c83e2d]"
                        }`}
                        data-testid={`complete-job-${j.id}`}
                        aria-label={j.status === "completed" ? "Reopen" : "Complete"}
                      >
                        {j.status === "completed" && <Check className="w-5 h-5 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <Link to={`/customers/${j.customer_id}`} className="font-display font-bold text-lg hover:text-[#e56b42]">
                            {j.customer_name}
                          </Link>
                          <span className="bm-badge bm-badge-scheduled">{j.service_type}</span>
                          {j.frequency !== "once" && (
                            <span className="bm-badge bm-badge-sent flex items-center gap-1">
                              <Repeat className="w-2.5 h-2.5" /> {j.frequency}
                            </span>
                          )}
                          {j.status === "completed" && <span className="bm-badge bm-badge-completed">Done</span>}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-1 text-sm text-[#b0c0b6]">
                          {j.scheduled_time && (
                            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#e56b42]" /> <span className="font-mono">{j.scheduled_time}</span> · {j.duration_minutes}m</div>
                          )}
                          {j.customer_phone && (
                            <a href={`tel:${j.customer_phone}`} className="flex items-center gap-2 hover:text-[#eae6db]">
                              <Phone className="w-3.5 h-3.5 text-[#e56b42]" /> <span className="font-mono">{j.customer_phone}</span>
                            </a>
                          )}
                          {j.customer_address && (
                            <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-3.5 h-3.5 text-[#e56b42]" /> <span>{j.customer_address}</span></div>
                          )}
                        </div>
                        {j.notes && <div className="text-xs text-[#8ba394] mt-2 italic">"{j.notes}"</div>}
                        <button
                          onClick={() => onOpenPhotos(j)}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#b0c0b6] hover:text-[#e56b42] border border-[#233129] hover:border-[#e56b42] px-2.5 py-1 rounded-sm transition-colors"
                          data-testid={`photos-job-${j.id}`}
                        >
                          <Camera className="w-3.5 h-3.5" />
                          <span>Photos</span>
                          {j.photos_count > 0 && (
                            <span className="font-mono font-bold text-[#e56b42]">· {j.photos_count}</span>
                          )}
                        </button>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {j.price > 0 && <div className="font-mono text-[#e56b42] font-bold">${Number(j.price).toFixed(0)}</div>}
                        <button onClick={() => onDelete(j)} className="text-[#8ba394] hover:text-[#c83e2d]" data-testid={`delete-job-${j.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CalendarView({ cursor, setCursor, jobsByDate, onAdd, onOpenDay }) {
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = new Date();

  return (
    <div>
      <div className="flex items-center justify-between mb-4 bm-card p-3">
        <button onClick={() => setCursor(subMonths(cursor, 1))} className="bm-btn bm-btn-outline" data-testid="cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-display font-black text-2xl tracking-tight" data-testid="cal-month-label">
          {format(cursor, "MMMM yyyy")}
        </div>
        <button onClick={() => setCursor(addMonths(cursor, 1))} className="bm-btn bm-btn-outline" data-testid="cal-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="overline text-center py-2 bg-[#0a0f0d] border border-[#233129]">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5" data-testid="calendar-grid">
        {days.map((d) => {
          const ymd = toYmd(d);
          const dayJobs = jobsByDate[ymd] || [];
          const isOther = !isSameMonth(d, cursor);
          const isToday = isSameDay(d, today);
          return (
            <div
              key={ymd}
              onClick={() => (dayJobs.length > 0 ? onOpenDay(d) : onAdd(d))}
              className={`bm-cal-cell ${isToday ? "is-today" : ""} ${isOther ? "is-other" : ""}`}
              data-testid={`cal-day-${ymd}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`font-mono text-sm ${isToday ? "text-[#c83e2d] font-bold" : "text-[#b0c0b6]"}`}>
                  {format(d, "d")}
                </span>
                {dayJobs.length > 0 && (
                  <span className="text-[10px] bg-[#1e3b2d] text-[#eae6db] px-1.5 py-0.5 font-mono">
                    {dayJobs.length}
                  </span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayJobs.slice(0, 2).map((j) => (
                  <div key={j.id} className={`text-[10px] px-1 py-0.5 truncate font-medium ${
                    j.status === "completed" ? "bg-[#2d6a4f]/30 text-[#b0c0b6]" : "bg-[#c83e2d]/20 text-[#eae6db]"
                  }`}>
                    {j.scheduled_time ? `${j.scheduled_time.slice(0, 5)} · ` : ""}{j.customer_name}
                  </div>
                ))}
                {dayJobs.length > 2 && (
                  <div className="text-[10px] text-[#8ba394]">+{dayJobs.length - 2} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
